import { yupResolver } from '@hookform/resolvers/yup';
import { useRouter } from 'next/router';
import { type ComponentProps, memo, useCallback, useId, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import * as Yup from 'yup';

import { type AnonymousAccountTrackingLocation } from '@air/analytics';
import { contentCollectionFormSelector, setSubmitterNameAction } from '@air/feature-content-collection/redux';
import { Button } from '@air/primitive-button';
import { Input } from '@air/primitive-input';
import { Label } from '@air/primitive-label';
import { useShortIdContext } from '@air/provider-short-id';
import { cn } from '@air/utils-tailwind';

import { useAnonymousSignUpForm } from '~/components/AnonymousSignUpForm/hooks/useAnonymousSignUpForm';
import { ReCaptchaTermsInfo } from '~/components/AnonymousSignUpForm/ReCaptchaTermsInfo';
import { useAnonymousSignUpModalLogic } from '~/components/PublicLinkAnonymousSignUpModal/hooks/useAnonymousSignUpModalLogic';

export const ContentCollectionOnboardingFormValidationSchema = Yup.object({
  name: Yup.string().required(),
  email: Yup.string().email().required(),
});

export const ContentCollectionOnboardingFormWithoutEmailValidationSchema = Yup.object({
  name: Yup.string().required(),
  email: Yup.string().email().optional(),
});

export type ContentCollectionOnboardingFormInput =
  | Yup.InferType<typeof ContentCollectionOnboardingFormValidationSchema>
  | Yup.InferType<typeof ContentCollectionOnboardingFormWithoutEmailValidationSchema>;

const trackLocation: AnonymousAccountTrackingLocation = 'content-collection';

export type ContentCollectionOnboardingFormProps = {
  requireAccount: boolean;
  onLoggedIn: () => void;
  onCompletedForm: () => void;
} & Pick<ComponentProps<'div'>, 'className'>;

export const ContentCollectionOnboardingForm = memo(
  ({ requireAccount, onLoggedIn, onCompletedForm, className }: ContentCollectionOnboardingFormProps) => {
    const formId = useId();
    const dispatch = useDispatch();
    const { shortId, objectId } = useShortIdContext();
    const { asPath } = useRouter();
    const { onFullLoginClick } = useAnonymousSignUpModalLogic({
      ssoCallbackData: { url: asPath },
      onLoginSuccess: onLoggedIn,
      hasTransparentOverlay: false,
    });
    const { onSignupWithEmailAndName } = useAnonymousSignUpForm({
      shortId,
      objectId,
      objectType: 'board',
      trackLocation,
      onLoggedIn,
    });

    const onSubmit = useCallback(
      async ({ email, name }: ContentCollectionOnboardingFormInput) => {
        dispatch(setSubmitterNameAction({ name }));

        if (requireAccount) {
          if (!email) {
            throw new Error('Email is required');
          }

          await onSignupWithEmailAndName(email, name);
        } else {
          onCompletedForm();
        }
      },
      [onCompletedForm, onSignupWithEmailAndName, requireAccount, dispatch],
    );

    const validationSchema = useMemo(
      () =>
        requireAccount
          ? ContentCollectionOnboardingFormValidationSchema
          : ContentCollectionOnboardingFormWithoutEmailValidationSchema,
      [requireAccount],
    );

    const {
      register,
      handleSubmit,
      formState: { isSubmitting, isValid, errors },
    } = useForm({
      mode: 'all',
      resolver: yupResolver(validationSchema),
      defaultValues: { email: '', name: '' },
    });

    const form = useSelector(contentCollectionFormSelector);

    return (
      <div
        data-testid="CONTENT_COLLECTION_ONBOARDING_FORM"
        className={cn('flex w-full max-w-[500px] flex-col gap-4 rounded-8 ads:bg-base bg-gray-1 p-8', className)}
      >
        <p className="font-medium ads:text-secondary text-16 text-gray-11">
          {requireAccount
            ? `To upload your files to ${form.workspace.name}, please share your name and email address.`
            : `To upload your files to ${form.workspace.name}, please share your name.`}
        </p>

        <form id={formId} className="flex w-full flex-col gap-4" onSubmit={handleSubmit(onSubmit)}>
          <div className="flex flex-col gap-1">
            <Label htmlFor={`${formId}-name`} className="pb-2 font-medium ads:text-secondary text-14 text-gray-11">
              Name
            </Label>
            <Input
              id={`${formId}-name`}
              autoFocus
              data-testid="CONTENT_COLLECTION_ONBOARDING_FORM_INPUT-name"
              size="extra-large"
              placeholder="Your name"
              hasError={!!errors.name}
              {...register('name')}
            />
          </div>

          {requireAccount && (
            <div className="flex flex-col gap-1">
              <Label htmlFor={`${formId}-email`} className="pb-2 font-medium ads:text-secondary text-14 text-gray-11">
                Email
              </Label>
              <Input
                id={`${formId}-email`}
                data-testid="CONTENT_COLLECTION_ONBOARDING_FORM_INPUT-email"
                size="extra-large"
                placeholder="email@domain.com"
                hasError={!!errors.email}
                {...register('email')}
              />
            </div>
          )}

          <div className="flex flex-col items-center justify-between gap-3 md:flex-row">
            <Button
              data-testid="CONTENT_COLLECTION_ONBOARDING_FORM_SUBMIT_BUTTON"
              type="submit"
              appearance="filled"
              color="blue"
              size="large"
              className="w-full rounded-8 md:w-auto"
              isLoading={isSubmitting}
              disabled={!isValid}
            >
              Submit your info
            </Button>
            <p className="ads:text-primary text-12 text-gray-12">
              Already on Air?{' '}
              <button
                data-testid="ANONYMOUS_SIGNUP_FORM_FULL_LOGIN_BUTTON"
                type="button"
                className="font-semibold ads:text-accent text-blue-10 hover:underline"
                onClick={onFullLoginClick}
              >
                Log In
              </button>
            </p>
          </div>

          <div>
            <ReCaptchaTermsInfo />
          </div>
        </form>
      </div>
    );
  },
);

ContentCollectionOnboardingForm.displayName = 'ContentCollectionOnboardingForm';
